/**
 * 板球(Cricket)相关的工具函数。
 * 所有计算都严格使用 BigMath 以保证精度。
 */
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { BigMath } from "./math";

// 扩展 dayjs 插件，供板球比赛状态判断使用
dayjs.extend( utc );
dayjs.extend( timezone );

/**
 * 板球球展示数据接口（对应前端 CricketBallVO）
 */
export interface CricketBallStyleData {
    display: string;
    type?: string;
    color?: string;
}

export interface CricketMatchRecordInning {
    /** 原始局分段，如 "202/6 (20 ov)" */
    raw: string;
    /** 得分 */
    runs: number;
    /** 出局数 */
    wickets: number;
    /** 出局数后的展示后缀，例如 "8d" 中的 "d"；未知后缀也原样保留 */
    wicketSuffix?: string;
    /** Over 展示文案，如 "20.0" */
    overDisplay: string;
    /** 是否全出局 */
    isAllOut: boolean;
    /** 标准展示文案，如 "165/10 (20.0)" */
    display: string;
}

export interface CricketMatchRecordSummary {
    /** 原始比分字符串 */
    raw: string;
    /** 首个比分分段，用于当前单行比分展示 */
    regularInning: CricketMatchRecordInning | null;
    /** 所有可解析的局分段，用于终局横幅总分计算 */
    innings: CricketMatchRecordInning[];
    /** 所有可解析局分段的总得分，用于终局横幅胜负计算 */
    totalRuns: number;
    /** 是否所有非空局分段都能解析，无法解析时不应使用 totalRuns 计算分差 */
    allInningsParsed: boolean;
}

export type CricketInningScoreSeparator = "/" | "-";

const CRICKET_OVER_VALUE_PATTERN = /\d+(?:\.\d+)?/;
const CRICKET_MATCH_RECORD_INNING_PATTERN = /^(\d+)[/-](\d+)([A-Za-z]*)\s*\(([^)]*)\)\s*$/i;

export function formatCricketInningScore(
    inning: Pick<CricketMatchRecordInning, "runs" | "wickets" | "wicketSuffix"> | null | undefined,
    separator: CricketInningScoreSeparator = "/",
) {
    if ( !inning ) return "";
    return `${inning.runs}${separator}${inning.wickets}${inning.wicketSuffix ?? ""}`;
}

export function formatCricketInningOver( inning: Pick<CricketMatchRecordInning, "overDisplay"> | null | undefined ) {
    if ( !inning ) return "";
    return inning.overDisplay;
}

export function formatCricketOversDisplay( inning: Pick<CricketMatchRecordInning, "overDisplay"> ): string {
    return formatCricketInningOver( inning );
}

function formatCricketOverText( overText: string ) {
    if ( !overText ) return "0.0";
    return BigMath.toFixed( overText, 1 );
}

function getCricketOverValueText( overText: string ) {
    return overText.trim().match( CRICKET_OVER_VALUE_PATTERN )?.[0] ?? "";
}

function parseCricketMatchRecordInning( part: string ): CricketMatchRecordInning | null {
    const raw = part.trim();
    if ( !raw ) return null;

    const match = raw.match( CRICKET_MATCH_RECORD_INNING_PATTERN );
    if ( !match ) return null;

    const runs = Number.parseInt( match[1], 10 );
    const wickets = Number.parseInt( match[2], 10 );
    const wicketSuffix = match[3] || undefined;
    const overValueText = getCricketOverValueText( match[4] );
    if ( !overValueText ) return null;

    if ( Number.isNaN( runs ) || Number.isNaN( wickets ) ) return null;

    const parsedInning = {
        raw,
        runs,
        wickets,
        wicketSuffix,
        overDisplay: formatCricketOverText( overValueText ),
        isAllOut: BigMath.gte( wickets, 10 ),
    };

    return {
        ...parsedInning,
        display: `${formatCricketInningScore( parsedInning )} (${formatCricketInningOver( parsedInning )})`,
    };
}

/**
 * 解析板球 matchRecord。
 *
 * 支持：
 * - "202/6 (20 ov)"
 * - "202/6 (20 overs)"
 * - "202/6 (20.0 over)"
 * - "564/8d (127 ov)"
 * - "564/8xxx (127 ov)"
 * - "202-6 (20.0)"
 * - "202/6 (20 ov)& 16/1 (1 ov)"
 * - "152/10 (58.4 ov) & 112/10 (35.5 ov)"
 *
 * 说明：
 * - innings/totalRuns 会保留所有可解析分段，用于终局横幅总分计算。
 * - matchRecord 只表达多段比分，不用于推断 Super Over。
 */
export function parseCricketMatchRecord( matchRecord: string | null | undefined ): CricketMatchRecordSummary {
    const raw = matchRecord?.trim() ?? "";
    const inningParts = raw.split( "&" ).map( part => part.trim() ).filter( Boolean );
    const regularInning = parseCricketMatchRecordInning( inningParts[0] ?? "" );
    const innings = inningParts.reduce<CricketMatchRecordInning[]>( ( parsedInnings, part ) => {
        const inning = parseCricketMatchRecordInning( part );
        if ( inning ) parsedInnings.push( inning );
        return parsedInnings;
    }, [] );
    const totalRuns = innings.reduce( ( total, inning ) => BigMath.plus( total, inning.runs, 0 ), 0 );

    return {
        raw,
        regularInning,
        innings,
        totalRuns,
        allInningsParsed: inningParts.length > 0 && inningParts.length === innings.length,
    };
}

/**
 * 板球比赛阶段。
 *
 * 说明：
 * 板球状态以 live-panel 的终局结果优先，其次看 Super Over 状态，最后回退到赛程时间：
 * - winningTeamId 和 winningTeam 都有内容 -> final
 * - superOverStatus 为 IN_PROGRESS -> live
 * - superOverStatus 为 COMPLETED -> final
 * - now < occurTime -> upcoming
 * - now >= occurTime && now <= endTime -> live
 * - now > endTime -> final
 *
 * 后续任何需要判断板球比赛状态的地方，都应统一调用这个方法。
 */
export type CricketMatchPhase = "upcoming" | "live" | "final";

/**
 * 判断实时比分面板是否已经返回有效获胜方。
 *
 * 只有 winningTeamId 和 winningTeam 同时有内容时，才认为后端已给出终局结果；
 * 单字段缺失时继续使用时间判断，避免半量数据把比赛误判为结束。
 */
function hasCricketWinningResult( livePanel: unknown ): boolean {
    if ( !livePanel || typeof livePanel !== "object" ) return false;

    const { winningTeamId, winningTeam } = livePanel as { winningTeamId?: unknown; winningTeam?: unknown };
    const hasWinningTeamId = typeof winningTeamId === "string"
        ? winningTeamId.trim().length > 0
        : winningTeamId !== null && winningTeamId !== undefined;
    const hasWinningTeam = !!winningTeam && typeof winningTeam === "object" && Object.keys( winningTeam ).length > 0;

    return hasWinningTeamId && hasWinningTeam;
}

function getCricketSuperOverStatus( livePanel: unknown ): "IN_PROGRESS" | "COMPLETED" | null {
    if ( !livePanel || typeof livePanel !== "object" ) return null;

    const { superOverStatus } = livePanel as { superOverStatus?: unknown };
    if ( superOverStatus === "IN_PROGRESS" || superOverStatus === "COMPLETED" ) return superOverStatus;

    return null;
}

/**
 * 计算板球单个 Over 的总得分
 *
 * @param balls 逐球列表
 * @returns 该 Over 的总得分
 */
export function calculateOverTotalScore( balls: { display: string; runs?: number }[] ): number {
    return balls.reduce( ( acc, ball ) => {
        // 如果后端直接返回了本球得分，则优先使用
        if ( ball.runs !== undefined ) {
            return BigMath.plus( acc, ball.runs, 0 );
        }

        const val = ball.display.toUpperCase();

        // 降级解析逻辑：
        // 1. 处理常见的附加分标识（W/NB/WD/LB/B）
        // 如果是纯字母 W, NB, WD，计 1 分
        if ( val === "W" || val === "NB" || val === "WD" ) return BigMath.plus( acc, 1, 0 );

        // 2. 处理带数字的附加分（如 "4wd", "1lb"）
        // 尝试从字符串中提取数字
        const numMatch = val.match( /\d+/ );
        if ( numMatch ) {
            const num = parseInt( numMatch[0], 10 );
            // 如果是宽球或无效球，通常还要加 1 分罚分
            const penalty = ( val.includes( "WD" ) || val.includes( "NB" ) ) ? 1 : 0;
            return BigMath.plus( acc, BigMath.plus( num, penalty, 0 ), 0 );
        }

        // 3. 纯数字解析
        const num = parseInt( val, 10 );
        return BigMath.plus( acc, isNaN( num ) ? 0 : num, 0 );
    }, 0 );
}

/**
 * 根据后端返回的 ball.color 和 ball.type 返回对应的样式类名（基于 Tailwind）
 *
 * 统一管理板球颜色规则（严格遵循 Figma 设计稿）：
 * 1. 六分 (6/6wd) → 蓝色 (#086DE0)
 * 2. 四分 (4/4wd) → 青色 (#13C2C2)
 * 3. 出局 (W) → 红色 (#EF4343)
 * 4. 其余 (0, 1, 2, lb, nb, wd 等) → 默认描边样式
 *
 * @param ball 球数据
 * @returns Tailwind 样式字符串
 */
export function getBallStyle( ball: CricketBallStyleData ): string {
    // 优先判定出局 (Wicket)
    const isWicket = ball.display === "W" || ball.type === "WICKET" || ball.display?.toUpperCase().includes( "W" );
    if ( isWicket ) {
        return "bg-[#EF4343] text-white border-transparent font-bold";
    }

    // 统一转大写进行逻辑判定
    const displayVal = ball.display?.toUpperCase() || "";
    const isSix = ball.color === "GREEN" || displayVal === "6" || displayVal.includes( "6" );
    const isFour = ball.color === "CYAN" || displayVal === "4" || displayVal.includes( "4" );

    if ( isSix ) {
        return "bg-[#086DE0] text-white border-transparent font-semibold"; // 六分 - 蓝色
    }
    if ( isFour ) {
        return "bg-[#13C2C2] text-white border-transparent font-semibold"; // 四分 - 青色
    }

    // 其余情况（含 WD、NB、LB 及普通得分）统一使用描边样式
    return "border border-[rgba(26,26,26,0.08)] bg-transparent text-[#1A1A1A] font-bold";
}

/**
 * 判断板球比赛当前阶段。
 *
 * 判断顺序：
 * 1. live-panel 已返回获胜方：直接视为 final
 * 2. Super Over 进行中：即使超过原 endTime，也继续视为 live
 * 3. Super Over 已完成：视为 final
 * 4. 未返回终局结果：按赛程时间判断 upcoming / live / final
 *
 * - 未开赛：当前时间早于开赛时间
 * - 进行中：当前时间落在开赛时间和结束时间之间
 * - 已结束：当前时间晚于结束时间
 *
 * @param occurTime - 开赛时间戳
 * @param endTime - 结束时间戳
 * @param now - 当前时间戳，默认使用印度时区时间戳
 * @param livePanel - 实时比分面板数据，用于优先判断是否已有获胜方
 */
export function getCricketMatchPhase(
    occurTime: number | null | undefined,
    endTime: number | null | undefined,
    now: number = dayjs().tz( "Asia/Kolkata" ).valueOf(),
    livePanel?: unknown,
): CricketMatchPhase {
    if ( hasCricketWinningResult( livePanel ) ) {
        return "final";
    }

    const superOverStatus = getCricketSuperOverStatus( livePanel );
    if ( superOverStatus === "IN_PROGRESS" ) {
        return "live";
    }
    if ( superOverStatus === "COMPLETED" ) {
        return "final";
    }

    const startTime = Number( occurTime ) || 0;
    const finishTime = Number( endTime ) || 0;

    if ( now < startTime ) {
        return "upcoming";
    }

    if ( now >= startTime && now <= finishTime ) {
        return "live";
    }

    return "final";
}

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

function pad2( value: number ): string {
    return String( value ).padStart( 2, "0" );
}

/**
 * 格式化板球未开赛倒计时。
 *
 * 规则：
 * - 剩余时间 >= 1 小时：时:分
 * - 剩余时间 < 1 小时：分:秒
 * - 0 或负数：显示 00m : 00s，避免页面在轮询刷新前出现空白
 */
export function formatCricketCountdown( remainingMs: number | null | undefined ): string {
    const safeRemainingMs = BigMath.lte( remainingMs, 0 ) ? 0 : remainingMs;
    const totalSeconds = BigMath.floor( BigMath.div( safeRemainingMs, MS_PER_SECOND, 0 ) );

    if ( BigMath.gte( safeRemainingMs, MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR ) ) {
        const hours = BigMath.floor( BigMath.div( totalSeconds, SECONDS_PER_MINUTE * MINUTES_PER_HOUR, 0 ) );
        const minutes = BigMath.floor(
            BigMath.div(
                BigMath.minus( totalSeconds, BigMath.times( hours, SECONDS_PER_MINUTE * MINUTES_PER_HOUR, 0 ) ),
                SECONDS_PER_MINUTE,
                0,
            ),
        );

        return `${pad2( hours )}h : ${pad2( minutes )}m`;
    }

    const minutes = BigMath.floor( BigMath.div( totalSeconds, SECONDS_PER_MINUTE, 0 ) );
    const seconds = BigMath.floor( BigMath.minus( totalSeconds, BigMath.times( minutes, SECONDS_PER_MINUTE, 0 ) ) );

    return `${pad2( minutes )}m : ${pad2( seconds )}s`;
}
